import React, { useState, useMemo, useContext } from 'react';
import styled from 'styled-components';
import {
  flex,
  FlexColumn,
  FlexColumnCenter,
  Modal,
  RadioButton,
  TimePicker as BaseTimePicker,
  Toggle,
  NumberInput as NumberInputBase
} from '@ubeya/shared-web/components';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useToaster from '@ubeya/shared/hooks/useToaster';

const Wrapper = styled(FlexColumnCenter)`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Buttons = styled(FlexColumn)`
  align-items: flex-start;
  .note {
    margin-bottom: 20px;
  }
`;

const Time = styled.div`
  display: flex;
  flex-direction: 'row';
  margin: 0 0 20px 30px;
`;

const TimeContainer = styled.div``;

const Toggler = styled.div`
  margin: 0 0 20px 30px;
`;

const Note = styled.div.attrs(() => ({ className: 'note' }))`
  margin-left: 30px;
`;

const TimePicker = styled(BaseTimePicker).attrs(() => ({ className: 'time-picker' }))`
  width: 160px;
  border-bottom: 1px solid black;
  line-height: 1;
  padding: 0;

  &.open {
    display: flex !important;
  }

  .select {
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .select-container {
    padding-right: 0;
    display: flex;
    justify-content: center;
  }

  .select-arrow {
    display: none;
  }

  .select-input,
  .select-label {
    padding: 0;
    min-height: 0;
  }

  .select-label {
    ${flex};
    max-width: unset;
    font-size: 15px;
    line-height: unset;
    overflow: unset !important;
    color: ${({ theme }) => theme.colors.black};
  }

  .select-menu {
    margin-top: -8px;
  }
`;

const Splitter = styled.div`
  margin-right: 5px;
  margin-bottom: 2px;
`;

const NumberInput = styled(NumberInputBase)`
  margin-left: 25px;
`;

const InformationNote = styled.div.attrs(() => ({ className: 'note' }))`
  font-size: larger;
  font-weight: bold;
  margin-bottom: 25px;
`;

const AutoFillModal = ({ onClose, data, autofill, autoFillCustomHours, autoFillBreaks, selectedProject, branchId }) => {
  const { t } = useContext(ConfigContext);
  const [autoFillChoice, setAutoFillChoice] = useState('scheduled');
  const [isLoading, setIsLoading] = useState(false);
  const { addSuccess, addResponseError } = useToaster();

  const { startTime, endTime } = selectedProject.shifts?.[0] || {};

  const [customHours, setCustomHours] = useState({ override: true });
  const [scheduledHours, setScheduledHours] = useState({ override: true });

  const [autoFillBreaksHours, setAutoFillBreaksHours] = useState(0);

  const {
    account: { hourFormat }
  } = useAccountConfig();

  const onConfirm = async () => {
    setIsLoading(true);
    setTimeout(async () => {
      try {
        if (autoFillChoice === 'scheduled') {
          await autofill({ ...data, ...scheduledHours });
        } else {
          if (autoFillChoice === 'custom') {
            await autoFillCustomHours({ ...customHours, branchId, projectId: selectedProject.id });
          }
          if (autoFillChoice === 'breaks') {
            await autoFillBreaks({ duration: autoFillBreaksHours });
          }
        }
        addSuccess(t(`success__update_timesheet_hours`));
      } catch (e) {
        addResponseError(e);
      } finally {
        onClose();
        setIsLoading(false);
      }
    }, 10);
  };

  const disabledConfirm = useMemo(() => {
    if (autoFillChoice === 'custom' && !customHours?.startTime) {
      return true;
    }
    if (autoFillChoice === 'breaks' && (!autoFillBreaksHours || autoFillBreaksHours === 0)) {
      return true;
    }
    return false;
  }, [autoFillBreaksHours, autoFillChoice, customHours?.startTime]);

  return (
    <Modal
      onClose={onClose}
      showConfirmButton
      confirmText={t('save')}
      loadingConfirm={isLoading}
      isPaddingContent={false}
      size="medium"
      disabledConfirm={disabledConfirm}
      title={t('autoFillTimesheet')}
      onConfirm={onConfirm}
      prompt={false}>
      <Wrapper>
        {!!data?.rows?.length && <InformationNote>{t(`autofillMultipleTimesheetsConfirmation`)}</InformationNote>}
        <Buttons>
          <RadioButton
            id="scheduled"
            value={autoFillChoice}
            onChange={setAutoFillChoice}
            content={t('fillByScheduledTimes')}
          />
          <Note>{t('autoFillTimesheetAccordingToScheduledHours')}</Note>

          {autoFillChoice === 'scheduled' && (
            <Toggler>
              <Toggle
                value={scheduledHours?.override}
                text={t('autoFillOverrideToggler')}
                onChange={(value) => setScheduledHours({ override: value })}
              />
            </Toggler>
          )}

          <RadioButton
            id="custom"
            value={autoFillChoice}
            onChange={setAutoFillChoice}
            content={t('fillByCustomTimes')}
          />
          <Note> {t('insertCustomTimesheetHoursToApplyForAllEmployees')}</Note>

          {autoFillChoice === 'custom' && (
            <TimeContainer>
              <Time>
                <TimePicker
                  value={customHours?.startTime || ''}
                  onChange={(newTime) => {
                    setCustomHours({
                      startTime: getTimeRangeWithDates({ date: selectedProject?.date, startTime: newTime })?.startTime,
                      endTime: customHours.endTime,
                      override: customHours.override
                    });
                  }}
                  onOutsideClickSave
                  $hourFormat={hourFormat}
                  defaultValue={startTime}
                  placeholder={t('start')}
                  usePortal
                />
                <Splitter>-</Splitter>
                <TimePicker
                  value={customHours?.endTime || ''}
                  onChange={(newTime) => {
                    setCustomHours({
                      endTime: getTimeRangeWithDates({
                        date: selectedProject?.date,
                        startTime: customHours.startTime,
                        endTime: newTime
                      })?.endTime,
                      startTime: customHours.startTime,
                      override: customHours.override
                    });
                  }}
                  defaultValue={endTime}
                  onOutsideClickSave
                  $hourFormat={hourFormat}
                  placeholder={t('end')}
                  usePortal
                />
              </Time>
              <Toggler>
                <Toggle
                  value={customHours?.override}
                  text={t('autoFillOverrideToggler')}
                  onChange={(value) => setCustomHours((prev) => ({ ...prev, override: value }))}
                />
              </Toggler>
            </TimeContainer>
          )}

          <RadioButton
            id="breaks"
            tooltipBody={t('noFilledHours')}
            value={autoFillChoice}
            onChange={setAutoFillChoice}
            content={t('fillBreaktimes')}
          />
          <Note> {t('updateBreaksForAll')}</Note>

          {autoFillChoice === 'breaks' && (
            <NumberInput
              value={autoFillBreaksHours}
              onChange={setAutoFillBreaksHours}
              title={t('breakDuration')}
              min={0}
            />
          )}
        </Buttons>
      </Wrapper>
    </Modal>
  );
};
export default AutoFillModal;
